import { useEffect } from 'react';
import { SITE } from '@/constants/site';

interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: string;
  jsonLd?: unknown;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function buildAbsolute(path: string) {
  const base = typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/';
  const prefix = base.replace(/\/$/, '');
  return `${window.location.origin}${prefix}${path}`;
}

export function useSeo({ title, description, keywords, path, type = 'website', jsonLd }: SeoOptions) {
  const ldString = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const fullTitle = title.includes(SITE.name) ? title : `${title} | ${SITE.name}`;
    const url = buildAbsolute(path);

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', 'index, follow');
    upsertLink('canonical', url);

    upsertMeta('property', 'og:site_name', SITE.name);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:locale', 'zh_CN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);

    const scriptId = 'guatian-jsonld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (ldString) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.text = ldString;
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script) script.remove();
    };
  }, [title, description, keywords, path, type, ldString]);
}